/* ============================================================
   Search results page — <search-results> custom element.

   Behaviors:
     1. Sort dropdown — change reloads with ?sort_by=<value>
     2. Infinite scroll — IntersectionObserver on [data-infinite-sentinel]
        fetches next page via Section Rendering API, appends new cards,
        updates the sentinel, hides fallback pagination.
     3. Clear button — wipes the input and submits an empty query (back
        to idle state).
   ============================================================ */

(() => {
  if (customElements.get('search-results')) return;

  class SearchResults extends HTMLElement {
    constructor() {
      super();
      this._loading = false;
    }

    connectedCallback() {
      this._sectionId = this.dataset.sectionId;
      this._sortSelect = this.querySelector('[data-search-sort]');
      this._clearBtn   = this.querySelector('[data-search-clear]');
      this._resultsWrap = this.querySelector('[data-results-wrap]');
      this._sentinel   = this.querySelector('[data-infinite-sentinel]');
      this._fallbackPagination = this.querySelector('[data-fallback-pagination]');
      this._loadingEl  = this.querySelector('[data-search-loading]');

      this._bindSort();
      this._bindClear();
      this._bindInfinite();
    }

    /* ============ Sort dropdown ============ */
    _bindSort() {
      if (!this._sortSelect) return;
      this._sortSelect.addEventListener('change', (e) => {
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', e.target.value);
        url.searchParams.delete('page'); // restart pagination
        window.location.assign(url.toString());
      });
    }

    /* ============ Clear button ============ */
    _bindClear() {
      if (!this._clearBtn) return;
      this._clearBtn.addEventListener('click', () => {
        const input = this.querySelector('input[name="q"]');
        if (input) input.value = '';
        const form = this.querySelector('[data-search-form]');
        if (form) form.submit();
      });
    }

    /* ============ Infinite scroll ============ */
    _bindInfinite() {
      if (!this._sentinel) return;
      if (!('IntersectionObserver' in window)) return;

      // Hide fallback pagination — JS will handle paging
      if (this._fallbackPagination) this._fallbackPagination.hidden = true;

      this._observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this._loading) {
            this._loadMore();
          }
        });
      }, { rootMargin: '400px 0px' });

      this._observer.observe(this._sentinel);
    }

    async _loadMore() {
      const nextUrl = this._sentinel?.dataset.nextUrl;
      if (!nextUrl) return;
      this._loading = true;
      this._setBusy(true);

      try {
        const url = new URL(nextUrl, window.location.origin);
        url.searchParams.set('section_id', this._sectionId);
        const res = await fetch(url.toString());
        if (!res.ok) throw new Error(`Search next page ${res.status}`);
        const html = await res.text();
        this._appendFromHtml(html);
      } catch (err) {
        console.warn('[search-results] load more failed', err);
        // Surface fallback pagination so the user can recover
        if (this._fallbackPagination) this._fallbackPagination.hidden = false;
      } finally {
        this._loading = false;
        this._setBusy(false);
      }
    }

    _appendFromHtml(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');

      // For each result group (products / articles / pages) in the new page,
      // grab its cards and append into the matching existing group. If the
      // group doesn't exist locally (e.g. first append for that type), clone
      // the whole group node.
      const newGroups = doc.querySelectorAll('[data-result-group]');
      newGroups.forEach((newGroup) => {
        const type = newGroup.dataset.resultGroup;
        const localGroup = this.querySelector(`[data-result-group="${type}"]`);

        const newCards = newGroup.querySelectorAll(
          type === 'products'
            ? '.product-card'
            : '.search-content-card'
        );

        if (localGroup) {
          const localGrid = localGroup.querySelector('.products-grid, .search-content-grid');
          if (localGrid) {
            const announced = [];
            newCards.forEach((card) => {
              localGrid.appendChild(card);
              announced.push(card);
            });
            // Briefly highlight new cards for a smoother UX (optional)
            announced.forEach((card) => {
              card.classList.add('search-card--appearing');
              requestAnimationFrame(() =>
                requestAnimationFrame(() => card.classList.remove('search-card--appearing'))
              );
            });
          }
        } else {
          // Insert before the sentinel
          if (this._sentinel && this._sentinel.parentNode) {
            this._sentinel.parentNode.insertBefore(newGroup, this._sentinel);
          }
        }
      });

      // Update or remove the sentinel based on whether the new page has one
      const newSentinel = doc.querySelector('[data-infinite-sentinel]');
      if (newSentinel) {
        this._sentinel.dataset.nextUrl = newSentinel.dataset.nextUrl;
      } else {
        // No more pages — done
        this._observer.disconnect();
        this._sentinel.remove();
        this._sentinel = null;
      }
    }

    _setBusy(busy) {
      if (this._resultsWrap) {
        this._resultsWrap.setAttribute('aria-busy', busy ? 'true' : 'false');
      }
      if (this._loadingEl) {
        this._loadingEl.hidden = !busy;
      }
    }
  }

  customElements.define('search-results', SearchResults);

  // Subtle pop-in for appended cards. The class is removed via rAF above.
  const style = document.createElement('style');
  style.textContent = `
    .search-card--appearing {
      opacity: 0;
      transform: translateY(6px);
    }
    .search-card--appearing,
    .search-card--appearing * {
      transition: opacity 280ms ease, transform 280ms ease;
    }
  `;
  document.head.appendChild(style);
})();
